import { useCallback, useState, useMemo, useRef, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Wrench, Zap, Wind, BookOpen, Download, Star, Sun, Moon, Trash2, Search, X, ChevronDown, Clock, HardHat, Mic, Heart, ArrowRight } from "lucide-react";
import { useRecentTools } from "@/hooks/use-recent-tools";
import CurrentJobCard from "@/components/CurrentJobCard";
import { toast } from "sonner";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, TouchSensor, useSensor, useSensors, type DragEndEvent, type DragOverEvent, type DragStartEvent } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { restrictToVerticalAxis, restrictToParentElement } from "@dnd-kit/modifiers";
import { useIsStandalone } from "@/hooks/use-standalone";
import { useFavorites, FavoriteItem } from "@/hooks/use-favorites";
import { useTheme } from "@/hooks/use-theme";
import SortableFavoriteCard from "@/components/SortableFavoriteCard";
import SwipeToDelete from "@/components/SwipeToDelete";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useSpeechSearch } from "@/hooks/use-speech-search";
import { mainTools, subTools, searchChips } from "@/lib/search-index";
import { TRADE_HUB } from "@/lib/trade-hub";

const favLinkMap: Record<FavoriteItem["type"], string> = {
  formula: "/electrical",
  pipe: "/pipe",
  wire: "/electrical",
  duct: "/ductwork",
  structural: "/structural",
};

const favGroupMeta: Record<FavoriteItem["type"], { label: string; icon: typeof Wrench }> = {
  pipe: { label: "Pipe Sizes", icon: Wrench },
  wire: { label: "Electrical", icon: Zap },
  formula: { label: "Formulas", icon: BookOpen },
  duct: { label: "Ductwork", icon: Wind },
  structural: { label: "Structural", icon: HardHat },
};

const allTools = mainTools;

const Index = () => {
  const isStandalone = useIsStandalone();
  const { favorites, removeFavoriteWithUndo, clearAllFavorites, reorderFavorites } = useFavorites();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [toolSearch, setToolSearch] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const [recentSearches, setRecentSearches] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem("trades-recent-searches") || "[]"); } catch { return []; }
  });
  const saveRecentSearch = useCallback((query: string) => {
    const trimmed = query.trim();
    if (!trimmed || trimmed.length < 3) return;
    setRecentSearches(prev => {
      const updated = [trimmed, ...prev.filter(s => s.toLowerCase() !== trimmed.toLowerCase())].slice(0, 3);
      localStorage.setItem("trades-recent-searches", JSON.stringify(updated));
      return updated;
    });
  }, []);
  const removeRecentSearch = useCallback((query: string) => {
    setRecentSearches(prev => {
      const updated = prev.filter(s => s !== query);
      localStorage.setItem("trades-recent-searches", JSON.stringify(updated));
      return updated;
    });
  }, []);
  const randomChips = useMemo(() => [...searchChips].sort(() => Math.random() - 0.5).slice(0, 5), []);
  const [searchParams, setSearchParams] = useSearchParams();
  const { recentTools, trackVisit, clearRecent } = useRecentTools();
  const { isListening, isSupported: speechSupported, toggle: toggleSpeech } = useSpeechSearch({
    onResult: (transcript) => setToolSearch(transcript),
  });

  useEffect(() => {
    if (isListening) toast.info("Listening… speak a tool name", { duration: 2000 });
  }, [isListening]);

  // Cmd+K / Ctrl+K or '/' to focus search
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const typing = e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement;
      const hotkey = ((e.metaKey || e.ctrlKey) && e.key === "k") || (e.key === "/" && !typing && !e.metaKey && !e.ctrlKey && !e.altKey);
      if (!hotkey) return;
      e.preventDefault();
      searchRef.current?.focus();
      searchRef.current?.select();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // Auto-focus when navigated with ?focus=search
  useEffect(() => {
    if (searchParams.get("focus") === "search") {
      setTimeout(() => { searchRef.current?.focus(); }, 100);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Smart search: scores results by relevance across labels, keywords, and problem descriptions
  const searchResults = useMemo(() => {
    const q = toolSearch.toLowerCase().trim();
    if (!q) return [];
    const words = q.split(/\s+/).filter(w => w.length > 1);
    if (!words.length) return [];

    const stopWords = new Set(["i", "a", "the", "to", "for", "of", "in", "do", "is", "my", "me", "it", "an", "on", "at", "we", "can"]);
    const meaningful = words.filter(w => !stopWords.has(w));
    const searchWords = meaningful.length ? meaningful : words;

    const scoreText = (text: string) => {
      const lower = text.toLowerCase();
      return (lower.includes(q) ? 10 : 0) + searchWords.reduce((s, w) => s + (lower.includes(w) ? 3 : 0), 0);
    };

    type ScoredResult = { path: string; label: string; desc: string; icon: typeof Wrench; color: string; toolId?: string; score: number };
    const results: ScoredResult[] = [];

    for (const t of allTools) {
      const score = scoreText(t.label) + scoreText(t.desc) + scoreText(t.keywords);
      if (score > 0) results.push({ path: t.path, label: t.label, desc: t.desc, icon: t.icon, color: t.color, score });
    }

    for (const t of subTools) {
      const parent = allTools.find(m => m.path === t.path);
      const score = scoreText(t.label) + scoreText(t.desc) + scoreText(t.keywords) + scoreText(t.problems || "");
      if (score > 0) {
        results.push({
          path: t.path,
          label: t.label,
          desc: `${t.parent} → ${t.desc.replace(/^Formulas → /, "")}`,
          icon: parent?.icon ?? BookOpen,
          color: parent?.color ?? "hsl(var(--steel-light))",
          toolId: t.toolId,
          score,
        });
      }
    }

    const seen = new Set<string>();
    return results
      .sort((a, b) => b.score - a.score)
      .filter(r => { if (seen.has(r.label)) return false; seen.add(r.label); return true; })
      .slice(0, 8);
  }, [toolSearch]);

  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 5) return "Working late";
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const [favsCollapsed, setFavsCollapsed] = useState(() => {
    try { return localStorage.getItem("trades-toolbelt-favs-collapsed") === "true"; } catch { return false; }
  });
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);

  const handleDragStart = useCallback((e: DragStartEvent) => setActiveId(String(e.active.id)), []);
  const handleDragOver = useCallback((e: DragOverEvent) => setOverId(e.over ? String(e.over.id) : null), []);
  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    setOverId(null);
    if (!over || active.id === over.id) return;
    const oldIndex = favorites.findIndex((f) => f.id === active.id);
    const newIndex = favorites.findIndex((f) => f.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    reorderFavorites(arrayMove(favorites, oldIndex, newIndex));
  }, [favorites, reorderFavorites]);
  const handleDragCancel = useCallback(() => { setActiveId(null); setOverId(null); }, []);

  return (
    <div className="pb-24 md:pb-8 animate-fade-in max-w-4xl mx-auto px-4">
      {/* Header */}
      <header className="pt-6 pb-3 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-extrabold tracking-tight text-foreground">
            {greeting}
          </h1>
          <p className="text-xs text-muted-foreground mt-1">
            Trades Toolbelt — every tool works offline
          </p>
        </div>
        <button
          onClick={toggleTheme}
          className="p-2 rounded-lg bg-secondary text-secondary-foreground hover:bg-secondary/80 transition-colors shrink-0"
          aria-label="Toggle theme"
        >
          {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </button>
      </header>

      {/* Search — the main way in */}
      <div className="relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" aria-hidden="true" />
          <input
            ref={searchRef}
            type="search"
            value={toolSearch}
            onChange={(e) => setToolSearch(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setTimeout(() => setSearchFocused(false), 200)}
            placeholder="Describe your problem or search tools… ⌘K"
            aria-label="Search tools by problem or name"
            role="combobox"
            aria-expanded={searchResults.length > 0}
            aria-autocomplete="list"
            className={`w-full bg-secondary border border-border rounded-xl pl-9 ${toolSearch || speechSupported ? "pr-9" : "pr-3"} py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all`}
          />
          {toolSearch ? (
            <button onClick={() => setToolSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label="Clear search">
              <X className="w-4 h-4" />
            </button>
          ) : speechSupported ? (
            <button
              onClick={toggleSpeech}
              className={`absolute right-3 top-1/2 -translate-y-1/2 transition-colors ${isListening ? "text-destructive animate-pulse" : "text-muted-foreground hover:text-foreground"}`}
              aria-label={isListening ? "Stop voice search" : "Search by voice"}
            >
              <Mic className="w-4 h-4" />
            </button>
          ) : null}
        </div>

        {searchFocused && !toolSearch.trim() && (
          <div className="mt-2 animate-fade-in space-y-2">
            {recentSearches.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5">
                <Clock className="w-3 h-3 text-muted-foreground" />
                {recentSearches.map((chip) => (
                  <span
                    key={chip}
                    className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 text-[11px] font-medium rounded-full bg-muted text-muted-foreground border border-border"
                  >
                    <button onMouseDown={(e) => e.preventDefault()} onClick={() => { setToolSearch(chip); searchRef.current?.focus(); }}>
                      {chip}
                    </button>
                    <button
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => removeRecentSearch(chip)}
                      className="p-0.5 rounded-full hover:bg-destructive/20 hover:text-destructive transition-colors"
                      aria-label={`Remove ${chip}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div className="flex flex-wrap gap-1.5">
              {randomChips.map((chip) => (
                <button
                  key={chip}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => { saveRecentSearch(chip); setToolSearch(chip); searchRef.current?.focus(); }}
                  className="px-2.5 py-1 text-[11px] font-medium rounded-full bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20 transition-colors"
                >
                  {chip}
                </button>
              ))}
            </div>
          </div>
        )}

        {searchResults.length > 0 && (
          <div role="listbox" aria-label="Search results" className="absolute left-0 right-0 top-full mt-1 bg-card border border-border rounded-xl shadow-lg overflow-hidden z-50">
            {searchResults.map((result, i) => (
              <button
                key={`${result.label}-${i}`}
                onClick={() => { saveRecentSearch(toolSearch); navigate(result.toolId ? `${result.path}?tool=${result.toolId}` : result.path); setToolSearch(""); }}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-secondary transition-colors border-b border-border last:border-b-0"
              >
                <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${result.color}15` }}>
                  <result.icon className="w-4 h-4" style={{ color: result.color }} />
                </div>
                <div>
                  <div className="text-sm font-medium text-foreground">{result.label}</div>
                  <div className="text-[11px] text-muted-foreground">{result.desc}</div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* What you're working on */}
      <CurrentJobCard />

      {/* Pick a trade */}
      <section className="mt-5">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Trades</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {TRADE_HUB.map((t) => (
            <Link
              key={t.id}
              to={t.calcPath}
              onClick={() => trackVisit(t.calcPath, t.label)}
              className="flex items-center gap-2.5 bg-card border border-border rounded-xl p-3 hover:border-primary/40 transition-colors active:scale-[0.98]"
            >
              <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <t.icon className="w-4.5 h-4.5 text-primary" />
              </div>
              <span className="text-sm font-semibold text-foreground leading-tight">{t.label}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* Recently used */}
      {recentTools.length > 0 && (
        <section className="mt-5">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Recent</h2>
            <button onClick={clearRecent} className="text-[11px] text-muted-foreground hover:text-destructive transition-colors">
              Clear
            </button>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
            {recentTools.map((recent) => {
              const meta = allTools.find(t => t.path === recent.path);
              const Icon = meta?.icon ?? Zap;
              const color = meta?.color ?? "hsl(var(--primary))";
              return (
                <Link
                  key={recent.path}
                  to={recent.path}
                  onClick={() => trackVisit(recent.path, recent.label)}
                  className="flex items-center gap-2 bg-card border border-border rounded-lg px-3 py-2 hover:border-primary/40 transition-colors active:scale-[0.97] shrink-0"
                >
                  <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
                    <Icon className="w-3.5 h-3.5" style={{ color }} />
                  </div>
                  <span className="text-xs font-semibold text-foreground whitespace-nowrap">{recent.label}</span>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* Favorites */}
      {favorites.length > 0 && (
        <section className="mt-5">
          <div className="flex items-center justify-between mb-2">
            <button
              onClick={() => setFavsCollapsed(prev => { const next = !prev; try { localStorage.setItem("trades-toolbelt-favs-collapsed", String(next)); } catch { /* ignore */ } return next; })}
              className="flex items-center gap-2"
              aria-expanded={!favsCollapsed}
            >
              <Star className="w-3.5 h-3.5 fill-primary text-primary" />
              <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Saved
                <span className="ml-2 text-[10px] font-bold bg-primary/15 text-primary rounded-full px-1.5 py-0.5 leading-none">{favorites.length}</span>
              </h2>
              <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${favsCollapsed ? "-rotate-90" : ""}`} />
            </button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-destructive transition-colors">
                  <Trash2 className="w-3 h-3" />
                  Clear all
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Clear all favorites?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will remove {favorites.length} favorite{favorites.length !== 1 ? "s" : ""}. You can undo this from the toast notification.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={clearAllFavorites} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                    Clear all
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>

          {!favsCollapsed && (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis, restrictToParentElement]}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDragEnd={handleDragEnd}
              onDragCancel={handleDragCancel}
            >
              <SortableContext items={favorites.map((f) => f.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-2">
                  {favorites.map((fav, i) => {
                    const showHeader = i === 0 || fav.type !== favorites[i - 1].type;
                    const meta = favGroupMeta[fav.type];
                    const activeIndex = activeId ? favorites.findIndex((f) => f.id === activeId) : -1;
                    const overIndex = overId ? favorites.findIndex((f) => f.id === overId) : -1;
                    const isOver = Boolean(activeId && overId && activeId !== overId && fav.id === overId);
                    return (
                      <div key={fav.id}>
                        {showHeader && (
                          <div className={`flex items-center gap-1.5 mb-1.5 ${i > 0 ? "mt-3" : ""}`}>
                            <meta.icon className="w-3.5 h-3.5 text-muted-foreground" />
                            <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{meta.label}</span>
                          </div>
                        )}
                        {isOver && activeIndex > overIndex && <div className="h-0.5 bg-primary rounded-full mx-2 my-1" />}
                        <SwipeToDelete onDelete={() => removeFavoriteWithUndo(fav)}>
                          <SortableFavoriteCard fav={fav} linkTo={favLinkMap[fav.type] || "/formulas"} />
                        </SwipeToDelete>
                        {isOver && activeIndex < overIndex && <div className="h-0.5 bg-primary rounded-full mx-2 my-1" />}
                      </div>
                    );
                  })}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </section>
      )}

      {/* Value 4 Value — keep the project free */}
      <Link
        to="/support"
        onClick={() => trackVisit("/support", "Value 4 Value")}
        className="mt-5 flex items-center gap-3 bg-gradient-to-r from-primary/10 via-card to-card border border-primary/20 rounded-xl p-3 hover:border-primary/40 transition-colors group"
      >
        <div className="w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center shrink-0">
          <Heart className="w-4 h-4 text-primary fill-primary/30 group-hover:fill-primary/50 transition-colors" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground">Value 4 Value</p>
          <p className="text-[11px] text-muted-foreground leading-tight">
            This app is free. If it saves you time on a job, give back what it's worth.
          </p>
        </div>
        <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
      </Link>

      {/* Install prompt — only when running in a browser */}
      {!isStandalone && (
        <Link
          to="/install"
          className="mt-3 flex items-center gap-3 bg-card border border-border rounded-xl p-3 hover:border-primary/40 transition-colors"
        >
          <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            <Download className="w-4 h-4 text-primary" />
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground">Install on this device</p>
            <p className="text-[11px] text-muted-foreground leading-tight">Keeps working with no signal</p>
          </div>
        </Link>
      )}
    </div>
  );
};

export default Index;
